import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { sha256 } from 'js-sha256';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public proxyUrl = environment.baseUrl;
    public _authenticated: boolean = false;
    private baseUrl = environment.baseUrl;
    
    // Observable for authentication state changes
    private _authenticationStatus = new BehaviorSubject<boolean>(false);
    public authenticationStatus$ = this._authenticationStatus.asObservable();

    //for seo
    public userEmailHash: string;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        // Initialize authentication status based on token existence
        if (this.accessToken && !AuthUtils.isTokenExpired(this.accessToken)) {
            this._authenticated = true;
            this._authenticationStatus.next(true);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        if (isPlatformBrowser(this.platformId)) {
            sessionStorage.setItem('accessToken', token);
        }
    }

    get accessToken(): string {
        if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
          return sessionStorage.getItem('accessToken') ?? '';
        }
        return '';
      }
      


  
    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Format phone number to ensure it starts with +27 instead of 0
     * @param phoneNumber The phone number to format
     * @returns Formatted phone number starting with +27
     */
    private formatMobileUsername(phoneNumber: any): string {
        if (!phoneNumber) {
            return '';
        }
        
        const phoneStr = String(phoneNumber);
        
        // If it starts with 0, replace with +27
        if (phoneStr.startsWith('0')) {
            return '+27' + phoneStr.slice(1);
        }
        
        // If it already starts with +27, return as is
        if (phoneStr.startsWith('+27')) {
            return phoneStr;
        }
        
        // Otherwise, cool
        return phoneStr;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {

        let headers = new HttpHeaders();
        headers = headers.append('Username', email);

        return this._httpClient.get(`${this.proxyUrl}/auth/forgotpassword`, { headers });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, token: string, email: string): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Token', token);
        headers = headers.append('Password', password);
        headers = headers.append('Username', email);

        return this._httpClient.get(`${this.proxyUrl}/auth/resetpassword`, { headers });
    }

    /**
     * Verify Email
     *
     * @param password
     */
    verifyEmailAddress(token: string): Observable<any> {

        return this._httpClient.get(`${this.proxyUrl}/service?url=endcustomer/verifyemailaddress?token=${token}`);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { Username: string; Password: string }): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Username', credentials.Username);
        headers = headers.append('Password', credentials.Password);
        headers = headers.append('AuthType', 'Customer');

        const options = {
            headers,
            responseType: 'text' as 'json'
        }
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.get(`${this.proxyUrl}/auth/token`, options).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response;
 

                // Set the authenticated flag to true
                this._authenticated = true;
                this.userEmailHash = sha256(credentials.Username);
                this._authenticationStatus.next(true);
                // console.log(this.userEmailHash);

                // Store the user on the user service
                //this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;
                this._authenticationStatus.next(true);

                // Store the user on the user service
                this._userService.user = response.user;
                // console.log(this._userService.user);

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Clear sessionStorage
        sessionStorage.clear();


        // Set the authenticated flag to false
        this._authenticated = false;
        this._authenticationStatus.next(false);

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { FirstName: string; Surname: string; EmailAddress: string; Password: string; IDNumber: string; MobileNumber: string; AlternativeMobileNumber?: string; MobileUsername?: string }): Observable<any> {
        user.MobileNumber = this.formatMobileUsername(user.MobileNumber);
        if (user.AlternativeMobileNumber) {
            user.AlternativeMobileNumber = this.formatMobileUsername(user.AlternativeMobileNumber);
        }
        if (user.MobileUsername) {
            user.MobileUsername = this.formatMobileUsername(user.MobileUsername);
        }

        return this._httpClient.post(`${this.baseUrl}/auth/register`, user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    /**
     * Check the authentication status
     */
    accountAvailability(EmailAddress: string, IDNumber: number, MobileUsername: number) {
        const data = {
            Data: {
                EmailAddress,
                IDNumber,
                MobileUsername: this.formatMobileUsername(MobileUsername)
            },
            Url: 'endcustomer/validateavailability'
        }

        return this._httpClient.post(`${this.proxyUrl}/service`, data);
    }

    /**
     * Register MobileUsername and send OTP
     */
    registerUsernameAndSendOTP(EmailAddress: string,) {
        const data = {
            Data: {
                EmailAddress,
            },
            Url: 'endcustomer/validateavailability_generateLoginOTP'
        }

        return this._httpClient.post(`${this.proxyUrl}/service`, data);
    }

    /**
     * Request OTP to mobile number
     */
    requestMobileOTP(MobileUsername: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));


        return this._httpClient.get(`${this.proxyUrl}/auth/requestMobileOTP`, { headers });
    }

    /**
     * Login using mobile OTP
     */
    loginWithOTP(MobileUsername: any, OTP: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        headers = headers.append('MobileOTP',  String(OTP));

        return this._httpClient.get(`${this.proxyUrl}/auth/token/mobile_otp`, { headers,  responseType: 'text'}).pipe(
            switchMap((response: any) => {
                    this.accessToken = response;
                    this._authenticated = true;
                    this._authenticationStatus.next(true);
                return of(response);
            }),
        );
    }

    /**
     * Sign up using mobile OTP
     */
    requestMobileOTPWithEmail(MobileUsername: any, FirstName: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        headers = headers.append('Name', FirstName);

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyRequestOTP?isEmail=true`, { headers });
    }

        requestMobileOTPWithMobile(MobileUsername: any, FirstName: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        headers = headers.append('Name', FirstName);

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyRequestOTP`, { headers });
    }


    requestMobileOTPWithoutEmail(MobileUsername: any, FirstName: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        headers = headers.append('Name', FirstName);

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyRequestOTP?isEmail=true`, { headers });
    }


    mobileVerifyRequestOTP(FirstName: string, isEmail: boolean, MobileUsername?: any, Email?: any) {
        let headers = new HttpHeaders();    
        if(isEmail){
            headers = headers.append('Username', String(Email));
        }else{
            headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        }
        headers = headers.append('Name', FirstName);


        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyRequestOTP?isEmail=${isEmail}`, { headers });
    }
    
    mobileVerifyConfirmOTP(MobileUsername: any, MobileOTP: any, VerificationID: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        headers = headers.append('MobileOTP', String(MobileOTP));
        headers = headers.append('VerificationID', String(VerificationID));

            return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyConfirmOTP`, {headers, responseType: 'text'});    
    }

    verifyConfirmExistingDetailsUpdateOTP(isEmail: boolean, MobileOTP: any, VerificationID: any, previousUsername: string, MobileUsername?: any, Email?: string) {
        let headers = new HttpHeaders();
        headers = headers.append('Username', String(isEmail ? Email : this.formatMobileUsername(MobileUsername)));
        headers = headers.append('OTP', String(MobileOTP));
        headers = headers.append('VerificationID', String(VerificationID));
        headers = headers.append('PreviousUsername', String(previousUsername));

            return this._httpClient.get(`${this.proxyUrl}/auth/confirmOTP/existinguser?updateAccountDetails=true&isEmail=${isEmail}`, {headers, responseType: 'text'});    
    }



    /*
     **********************************************************************
     *************************** Campaigns *********************************
     **********************************************************************
     */

    /**
     * Sign up using email OTP
     */
    requestEmailOTPWithEmail(MobileUsername: any, FirstName: any) {
        let headers = new HttpHeaders();
        headers = headers.append('Username', this.formatMobileUsername(MobileUsername));
        headers = headers.append('Name', String(FirstName));

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyRequestOTP?isEmail=true`, { headers });
    }

    /**
     * confirm email OTP
     */
    emailVerifyConfirmOTP(MobileUsername: any, MobileOTP: any, VerificationID: any) {
        let headers = new HttpHeaders();
        headers = headers.append('Username', this.formatMobileUsername(MobileUsername));
        headers = headers.append('OTP', String(MobileOTP));
        headers = headers.append('VerificationID', String(VerificationID));
        // console.log({headers})

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyConfirmOTP`, {headers,  responseType: 'text'});
    }


    /**
     * confirm mobile OTP
     */
    mobile_VerifyConfirmOTP(MobileUsername: any, MobileOTP: any, VerificationID: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', this.formatMobileUsername(MobileUsername));
        headers = headers.append('MobileOTP', String(MobileOTP));
        headers = headers.append('VerificationID', String(VerificationID));
        // console.log({headers})

        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyConfirmOTP`, {headers,  responseType: 'text'});
    }



}
